import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { subjectsApi, Subject, Topic } from '../../../api/subjects';
import { SubjectFormData, TopicFormData, UseSubjectsReturn } from '../types';

const initialSubjectForm: SubjectFormData = {
  slug: '',
  name: '',
  name_ru: '',
  name_kiril: '',
  icon: '',
  sort_order: 0,
};

const initialTopicForm: TopicFormData = {
  subject_id: '',
  slug: '',
  name: '',
  name_ru: '',
  name_kiril: '',
  sort_order: 0,
};

export function useSubjects(): UseSubjectsReturn {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [formData, setFormData] = useState<SubjectFormData>(initialSubjectForm);

  const [topicsBySubject, setTopicsBySubject] = useState<Record<string, Topic[]>>({});
  const [expandedSubjectId, setExpandedSubjectId] = useState<string | null>(null);
  const [isTopicDialogOpen, setIsTopicDialogOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null);
  const [topicFormData, setTopicFormData] = useState<TopicFormData>(initialTopicForm);

  const fetchSubjects = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await subjectsApi.getAll();
      setSubjects(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch subjects');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  const handleCreate = async () => {
    try {
      await subjectsApi.create({
        ...formData,
        name_ru: formData.name_ru || null,
        name_kiril: formData.name_kiril || null,
        icon: formData.icon || null,
      });
      toast.success('Subject created');
      setIsCreateDialogOpen(false);
      setFormData(initialSubjectForm);
      fetchSubjects();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create subject');
    }
  };

  const handleEdit = (subject: Subject) => {
    setEditingSubject(subject);
    setFormData({
      slug: subject.slug,
      name: subject.name,
      name_ru: subject.name_ru || '',
      name_kiril: subject.name_kiril || '',
      icon: subject.icon || '',
      sort_order: subject.sort_order,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingSubject) return;
    try {
      await subjectsApi.update(editingSubject.id, {
        name: formData.name,
        name_ru: formData.name_ru || null,
        name_kiril: formData.name_kiril || null,
        icon: formData.icon || null,
        sort_order: formData.sort_order,
      });
      toast.success('Subject updated');
      setIsEditDialogOpen(false);
      setEditingSubject(null);
      fetchSubjects();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update subject');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this subject? This will deactivate it.')) return;
    try {
      await subjectsApi.delete(id);
      toast.success('Subject deleted');
      fetchSubjects();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete subject');
    }
  };

  const handleToggleTopics = async (subjectId: string) => {
    if (expandedSubjectId === subjectId) {
      setExpandedSubjectId(null);
      return;
    }
    setExpandedSubjectId(subjectId);
    if (!topicsBySubject[subjectId]) {
      try {
        const topics = await subjectsApi.getTopics(subjectId);
        setTopicsBySubject((prev) => ({ ...prev, [subjectId]: topics }));
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to fetch topics');
      }
    }
  };

  const refreshTopics = async (subjectId: string) => {
    try {
      const topics = await subjectsApi.getTopics(subjectId);
      setTopicsBySubject((prev) => ({ ...prev, [subjectId]: topics }));
    } catch {
      // ignore
    }
  };

  const handleOpenCreateTopic = (subjectId: string) => {
    setEditingTopic(null);
    setTopicFormData({ ...initialTopicForm, subject_id: subjectId });
    setIsTopicDialogOpen(true);
  };

  const handleEditTopic = (topic: Topic) => {
    setEditingTopic(topic);
    setTopicFormData({
      subject_id: topic.subject_id,
      slug: topic.slug,
      name: topic.name,
      name_ru: topic.name_ru || '',
      name_kiril: topic.name_kiril || '',
      sort_order: topic.sort_order,
    });
    setIsTopicDialogOpen(true);
  };

  const handleSaveTopic = async () => {
    try {
      if (editingTopic) {
        await subjectsApi.updateTopic(editingTopic.id, {
          name: topicFormData.name,
          name_ru: topicFormData.name_ru || null,
          name_kiril: topicFormData.name_kiril || null,
          sort_order: topicFormData.sort_order,
        });
        toast.success('Topic updated');
      } else {
        await subjectsApi.createTopic({
          ...topicFormData,
          name_ru: topicFormData.name_ru || null,
          name_kiril: topicFormData.name_kiril || null,
        });
        toast.success('Topic created');
      }
      setIsTopicDialogOpen(false);
      setEditingTopic(null);
      await refreshTopics(topicFormData.subject_id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save topic');
    }
  };

  const handleDeleteTopic = async (topic: Topic) => {
    if (!window.confirm(`Delete topic "${topic.name}"?`)) return;
    try {
      await subjectsApi.deleteTopic(topic.id);
      toast.success('Topic deleted');
      await refreshTopics(topic.subject_id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete topic');
    }
  };

  return {
    subjects,
    isLoading,
    isCreateDialogOpen,
    isEditDialogOpen,
    editingSubject,
    formData,
    topicsBySubject,
    expandedSubjectId,
    isTopicDialogOpen,
    editingTopic,
    topicFormData,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    setIsTopicDialogOpen,
    setTopicFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleToggleTopics,
    handleOpenCreateTopic,
    handleEditTopic,
    handleSaveTopic,
    handleDeleteTopic,
  };
}
